import { useEffect, useState, useMemo } from "react";
import ProductCard from "./ProductCard";
import { api } from "../api/axios";
import axios from "axios";
import Footer from "../Pages/Footer/Footer";
import { useSearchParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useProductTranslation } from "../../i18n/useProductTranslation";

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function Products() {
  const { t, translateCategory } = useProductTranslation();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [selectedCategories, setSelectedCategories] = useState([]);

  useEffect(() => {
    const cat = searchParams.get("category");
    setSelectedCategories(cat ? cat.split(",").filter(Boolean) : []);
    setCurrentPage(1);
  }, [searchParams, location.state]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});

  const fetchProducts = async (page = 1, name = "", categorySlugs = []) => {
    try {
      setLoading(true);
      const res = await api.get("/api/v1/products", {
        params: { page, name, category: categorySlugs.join(",") },
      });
      const data = res.data;
      setProducts(data.data || []);
      setTotalPages(data.last_page || 1);
      setCurrentPage(data.current_page || 1);
    } catch (error) {
      console.error("Erro ao buscar produtos:", error.response || error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/v1/categories`);
      let cats = res.data.data || [];

      // constrói o mapa
      const map = {};
      cats.forEach((cat) => {
        cat.children = [];
        map[cat.id] = cat;
      });

      // agrupa filhos nos pais
      cats.forEach((cat) => {
        if (cat.parent_id && map[cat.parent_id]) {
          map[cat.parent_id].children.push(cat);
        }
      });

      // ordena pais por position
      const roots = cats
        .filter((cat) => !cat.parent_id)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

      // ordena filhos de cada pai por position
      roots.forEach((cat) => {
        cat.children.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      });

      setCategories(roots);

      const expanded = {};
      roots.forEach((cat) => { expanded[cat.id] = true; });
      setExpandedCategories(expanded);
    } catch (err) {
      console.error("Erro ao buscar categorias:", err.response || err.message);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts(currentPage, searchName, selectedCategories);
  }, [currentPage, searchName, selectedCategories]);

  useEffect(() => {
    const delay = setTimeout(() => setCurrentPage(1), 200);
    return () => clearTimeout(delay);
  }, [searchName, selectedCategories]);

  const toggleCategory = (slug) => {
    setSelectedCategories((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
    setCurrentPage(1);
  };

  const toggleExpand = (id) => {
    setExpandedCategories((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleParentCategory = (cat) => {
    const childSlugs = (cat.children || []).map((c) => c.slug);
    const allSlugs = [cat.slug, ...childSlugs];
    const allActive = allSlugs.every((s) => selectedCategories.includes(s));

    if (allActive) {
      setSelectedCategories((prev) => prev.filter((s) => !allSlugs.includes(s)));
    } else {
      setSelectedCategories((prev) => {
        const next = new Set(prev);
        allSlugs.forEach((s) => next.add(s));
        return [...next];
      });
    }
    setCurrentPage(1);
  };

  const isParentActive = (cat) => {
    return selectedCategories.includes(cat.slug);
  };

  const getVisiblePages = () => {
    const maxVisible = 7;
    const pages = [];
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = start + maxVisible - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <Helmet>
        <title>{t("Produtos | Racksforall")}</title>
        <meta
          name="description"
          content={t("Explora o catálogo de estantes metálicas, racks industriais e soluções de armazenagem. Filtra por categoria e encontra a solução ideal para o teu espaço.")}
        />
        <link rel="canonical" href="https://racksforall.pt/products" />
        <meta property="og:title" content={t("Produtos | Racksforall")} />
        <meta
          property="og:description"
          content={t("Catálogo completo de estantes metálicas e racks industriais com entrega rápida em Portugal.")}
        />
        <meta property="og:url" content="https://racksforall.pt/products" />
        <meta property="og:image" content="https://racksforall.pt/racks-modulares.jpg" />
      </Helmet>
      {/* compensar navbar fixa (h-16) */}
      <div className="pt-20 pb-12 px-4 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-7xl">

          {/* Page header */}
          <div className="mb-10">
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-gray-500">
              {t("Catálogo")}
            </p>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900">
              {t("Os Nossos Produtos")}
            </h1>
            <p className="mt-3 max-w-2xl text-gray-600">
              {t("Explora os nossos produtos, pesquisa por nome e filtra por categoria para encontrares exatamente o que procuras.")}
            </p>
          </div>

          <div className="flex gap-8 items-start">

            {/* Sidebar */}
            <aside className="hidden lg:flex flex-col w-64 shrink-0 sticky top-24 self-start">
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    {t("Categorias")}
                  </span>
                  {selectedCategories.length > 0 && (
                    <button
                      onClick={() => setSelectedCategories([])}
                      className="text-xs text-gray-400 hover:text-gray-700 transition font-medium"
                    >
                      {t("Limpar")}
                    </button>
                  )}
                </div>

                {/* Todas */}
                <div className="px-3 pt-3">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCategories([]);
                      setCurrentPage(1);
                    }}
                    className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${selectedCategories.length === 0
                      ? "bg-gray-900 text-white"
                      : "text-gray-600 hover:bg-gray-50"
                      }`}
                  >
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-md text-xs ${selectedCategories.length === 0
                        ? "bg-white/20 text-white"
                        : "bg-gray-100 text-gray-500"
                        }`}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <rect x="3" y="3" width="7" height="7" rx="1" />
                        <rect x="14" y="3" width="7" height="7" rx="1" />
                        <rect x="3" y="14" width="7" height="7" rx="1" />
                        <rect x="14" y="14" width="7" height="7" rx="1" />
                      </svg>
                    </span>
                    {t("Todas as categorias")}
                  </button>
                </div>

                {/* Árvore de categorias */}
                <nav className="px-3 pb-3 pt-1 space-y-1">
                  {categories.map((cat) => {
                    const isExpanded = expandedCategories[cat.id];
                    const isActive = isParentActive(cat);
                    const hasChildren = cat.children && cat.children.length > 0;

                    return (
                      <div key={cat.id}>
                        <div
                          className={`flex items-center gap-1 rounded-xl transition-all group ${isActive ? "bg-gray-100" : "hover:bg-gray-50"
                            }`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleCategory(cat.slug)}
                            className={`flex flex-1 items-center gap-3 px-3 py-2.5 text-sm font-medium text-left transition-colors ${isActive ? "text-gray-900 font-semibold" : "text-gray-700"
                              }`}
                          >
                            <span
                              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-all ${isActive
                                ? "border-gray-900 bg-gray-900"
                                : "border-gray-300 group-hover:border-gray-400"
                                }`}
                            >
                              {isActive && (
                                <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                                  <path
                                    d="M2 6l3 3 5-5"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </span>
                            <span className="truncate">{translateCategory(cat.slug) || cat.name}</span>
                          </button>

                          {hasChildren && (
                            <button
                              type="button"
                              onClick={() => toggleExpand(cat.id)}
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg mr-1 transition-colors ${isActive
                                ? "text-indigo-400 hover:bg-indigo-100"
                                : "text-gray-400 hover:bg-gray-200"
                                }`}
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                  transition: "transform 0.2s",
                                }}
                              >
                                <path d="m6 9 6 6 6-6" />
                              </svg>
                            </button>
                          )}
                        </div>

                        {hasChildren && isExpanded && (
                          <div className="ml-4 mt-0.5 mb-1 space-y-0.5 border-l-2 border-gray-100 pl-3">
                            {cat.children.map((sub) => {
                              const subActive = selectedCategories.includes(sub.slug);
                              return (
                                <button
                                  key={sub.id}
                                  type="button"
                                  onClick={() => toggleCategory(sub.slug)}
                                  className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-all ${subActive
                                    ? "bg-gray-100 text-gray-900 font-semibold"
                                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                                    }`}
                                >
                                  <span
                                    className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-all ${subActive
                                      ? "border-gray-900 bg-gray-900"
                                      : "border-gray-300"
                                      }`}
                                  >
                                    {subActive && (
                                      <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
                                        <path
                                          d="M2 6l3 3 5-5"
                                          stroke="white"
                                          strokeWidth="2.2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    )}
                                  </span>
                                  <span className="truncate">{translateCategory(sub.slug) || sub.name}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </nav>
              </div>
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0">
              {/* Search + count */}
              <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-sm backdrop-blur sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">
                    {t("Produtos disponíveis")}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {loading
                      ? t("A carregar resultados…")
                      : t(`${products.length} produto(s) nesta página`)}
                  </p>
                </div>

                <div className="relative w-full sm:w-72">
                  <input
                    type="text"
                    placeholder={t("Pesquisar produto…")}
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 pr-10 text-sm text-gray-700 shadow-sm outline-none transition focus:border-gray-400 focus:ring-4 focus:ring-gray-100"
                  />
                  {searchName ? (
                    <button
                      onClick={() => setSearchName("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                    </button>
                  ) : (
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.3-4.3" />
                      </svg>
                    </span>
                  )}
                </div>
              </div>

              {/* filtros ativos */}
              {selectedCategories.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {selectedCategories.map((slug) => {
                    const allCats = categories.flatMap((c) => [c, ...(c.children || [])]);
                    const cat = allCats.find((c) => c.slug === slug);
                    return cat ? (
                      <span
                        key={slug}
                        className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-3 py-1 text-xs font-medium text-white"
                      >
                        {translateCategory(cat.slug) || cat.name}
                        <button
                          onClick={() => toggleCategory(slug)}
                          className="hover:text-indigo-900 transition"
                        >
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M18 6 6 18" />
                            <path d="m6 6 12 12" />
                          </svg>
                        </button>
                      </span>
                    ) : null;
                  })}
                  <button
                    onClick={() => setSelectedCategories([])}
                    className="text-xs text-gray-400 hover:text-gray-700 transition font-medium px-1"
                  >
                    {t("Limpar tudo")}
                  </button>
                </div>
              )}

              {/* grid de produtos */}
              {loading ? (
                <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-80 animate-pulse rounded-2xl border border-gray-200 bg-white shadow-sm"
                    />
                  ))}
                </div>
              ) : products.length === 0 ? (
                <div className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white text-center shadow-sm">
                  <div className="mb-3 rounded-full bg-gray-100 p-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8 text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.8}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M20.25 7.5 12 3 3.75 7.5M20.25 7.5 12 12m8.25-4.5v9L12 21m8.25-13.5L12 12m0 9L3.75 16.5v-9M12 21v-9m0 0L3.75 7.5"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {t("Nenhum produto encontrado")}
                  </h3>
                  <p className="mt-2 max-w-md text-sm text-gray-500">
                    {t("Tenta ajustar o nome pesquisado ou remover alguns filtros de categoria.")}
                  </p>
                </div>
              ) : (
                <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className="transition duration-300 hover:-translate-y-1"
                    >
                      <ProductCard product={product} />
                    </div>
                  ))}
                </div>
              )}

              {/* paginação */}
              {totalPages > 1 && (
                <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                    className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t("Anterior")}
                  </button>

                  {getVisiblePages().map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`min-w-[42px] rounded-xl px-4 py-2.5 text-sm font-medium transition ${currentPage === page
                        ? "bg-gray-900 text-white shadow-md"
                        : "border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50"
                        }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t("Próxima")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}